import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, Plus } from 'lucide-react';

import { BrixLogo } from '@/components/brand/BrixLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SignaturePad, type SignaturePadHandle } from '@/components/audit/SignaturePad';
import {
  ApplyBackdrop,
  type AttachedDoc,
  DocUpload,
  EMPTY_ADDR,
  Field,
  Section,
  type StructuredAddr,
  StructuredAddressField,
  Textarea,
  addrBlob,
  addrComplete,
  addrEmpty,
  addrJson,
} from '@/components/apply/ApplyFields';
import { APPLICATION_TERMS, TERMS_VERSION } from '@/lib/applicationTerms';
import { SALES_TAX_WARNING, applicationTaxError, needsSalesTaxAck } from '@/lib/applicationTax';
import { SUPPORT_PHONE, SUPPORT_TEL_HREF } from '@/lib/constants';

/**
 * /apply — public self-serve new-customer application. No login, no app shell
 * (a sibling of /login in the router). Posts to the onboarding-submit Netlify
 * function, which stores the request + uploads documents and emails the team
 * + the applicant.
 *
 * Expanded 2026-07-09 (Sky): accounting contact, role contacts (billing /
 * orders / management), DUNS, Tax ID + resale certificate uploads, delivery
 * windows, special instructions, optional credit application (terms + three
 * trade references), and Terms & Conditions acceptance.
 *
 * Spam protection: hidden `hp_field` honeypot + optional Cloudflare Turnstile
 * (rendered only when VITE_TURNSTILE_SITE_KEY is set).
 */

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: { sitekey: string; callback: (token: string) => void; 'expired-callback'?: () => void },
  ) => string;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

interface RoleContact {
  name: string;
  email: string;
  phone: string;
}

interface CreditRef {
  company: string;
  contact: string;
  phone: string;
  email: string;
}

const EMPTY_CONTACT: RoleContact = { name: '', email: '', phone: '' };
const EMPTY_REF: CreditRef = { company: '', contact: '', phone: '', email: '' };

interface FormState {
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  business_type: string;
  est_volume: string;
  duns_number: string;
  tax_id: string;
  resale_permit_number: string;
  accounting_contact: string;
  accounting_email: string;
  delivery_windows: string;
  special_instructions: string;
  notes: string;
  credit_terms: string;
  entity_type: string;
  years_in_business: string;
  bank_name: string;
  authorized_by: string;
}

const EMPTY: FormState = {
  business_name: '',
  contact_name: '',
  email: '',
  phone: '',
  business_type: '',
  est_volume: '',
  duns_number: '',
  tax_id: '',
  resale_permit_number: '',
  accounting_contact: '',
  accounting_email: '',
  delivery_windows: '',
  special_instructions: '',
  notes: '',
  credit_terms: 'Net 30',
  entity_type: '',
  years_in_business: '',
  bank_name: '',
  authorized_by: '',
};

export default function Apply() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [contacts, setContacts] = useState<Record<'billing' | 'orders' | 'management', RoleContact>>({
    billing: { ...EMPTY_CONTACT },
    orders: { ...EMPTY_CONTACT },
    management: { ...EMPTY_CONTACT },
  });
  const [creditRequested, setCreditRequested] = useState(false);
  const [refs, setRefs] = useState<CreditRef[]>([{ ...EMPTY_REF }, { ...EMPTY_REF }, { ...EMPTY_REF }]);
  const [taxIdDoc, setTaxIdDoc] = useState<AttachedDoc | null>(null);
  const [resaleDoc, setResaleDoc] = useState<AttachedDoc | null>(null);
  /** "I understand I will be charged sales tax" — only asked when no permit # or certificate (1.171). */
  const [salesTaxAck, setSalesTaxAck] = useState(false);
  /** Shown inside the tax section itself — the page scrolls there on a miss. */
  const [taxFieldError, setTaxFieldError] = useState<string | null>(null);
  const ackMissing = !!taxFieldError && taxFieldError.startsWith(SALES_TAX_WARNING);
  const taxSectionRef = useRef<HTMLDivElement>(null);
  const showTaxAck = needsSalesTaxAck({
    resalePermitNumber: form.resale_permit_number,
    hasResaleCertificate: !!resaleDoc,
  });
  const [billingAddr, setBillingAddr] = useState<StructuredAddr>({ ...EMPTY_ADDR });
  const [deliveryAddrs, setDeliveryAddrs] = useState<StructuredAddr[]>([{ ...EMPTY_ADDR }]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const sigRef = useRef<SignaturePadHandle | null>(null);
  const [signed, setSigned] = useState(false);
  const [hpField, setHpField] = useState(''); // honeypot — must stay empty
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite'); // set when arriving via a staff-sent invite link
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const turnstileRef = useRef<HTMLDivElement | null>(null);

  // Load + render the Turnstile widget only when a site key is configured.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    let rendered = false;

    function tryRender() {
      if (rendered || !turnstileRef.current || !window.turnstile) return;
      rendered = true;
      window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY!,
        callback: (token: string) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
      });
    }

    if (window.turnstile) {
      tryRender();
      return;
    }
    let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', tryRender);
    const poll = window.setInterval(tryRender, 300);
    return () => {
      script?.removeEventListener('load', tryRender);
      window.clearInterval(poll);
    };
  }, []);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'tax_id' || key === 'resale_permit_number') setTaxFieldError(null);
  }
  function updateContact(role: 'billing' | 'orders' | 'management', key: keyof RoleContact, value: string) {
    setContacts((c) => ({ ...c, [role]: { ...c[role], [key]: value } }));
  }
  function updateRef(i: number, key: keyof CreditRef, value: string) {
    setRefs((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: value } : row)));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.business_name.trim()) return setError('Please enter your business name.');
    if (!form.contact_name.trim()) return setError('Please enter a contact name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return setError('Please enter a valid email address.');
    }
    // Tax ID is required; no resale permit/certificate needs an explicit
    // "I understand" about sales tax (1.171). Same rule the server enforces.
    const taxError = applicationTaxError({
      taxId: form.tax_id,
      resalePermitNumber: form.resale_permit_number,
      hasResaleCertificate: !!resaleDoc,
      salesTaxAcknowledged: salesTaxAck,
    });
    setTaxFieldError(taxError);
    if (taxError) {
      taxSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return setError(taxError);
    }
    if (creditRequested) {
      const complete = refs.filter((r) => r.company.trim() && (r.phone.trim() || r.email.trim()));
      if (complete.length < 3) {
        return setError('A credit application needs three trade references (company plus a phone or email for each).');
      }
    }
    if (!addrComplete(billingAddr)) {
      return setError('Please complete the billing address — street, city, state and ZIP.');
    }
    const deliveries = deliveryAddrs.filter((a) => !addrEmpty(a));
    for (const a of deliveries) {
      if (!addrComplete(a)) {
        return setError('Each delivery location needs a street, city, state and ZIP (remove any you are not using).');
      }
    }
    // No delivery locations entered → deliveries default to the billing address.
    const effectiveDeliveries = deliveries.length > 0 ? deliveries : [billingAddr];
    if (!termsAccepted) return setError('Please review and accept the Terms & Conditions.');
    if (!form.authorized_by.trim()) {
      return setError('Please enter the name and title of the person authorizing this application.');
    }
    const signatureUrl = sigRef.current?.toDataURL() ?? null;
    if (!signatureUrl) return setError('Please sign the application in the signature box.');

    setSubmitting(true);
    try {
      const res = await fetch('/.netlify/functions/onboarding-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          contacts,
          tax_id_doc: taxIdDoc,
          resale_cert_doc: resaleDoc,
          sales_tax_acknowledged: showTaxAck && salesTaxAck,
          credit_requested: creditRequested,
          credit_terms: creditRequested ? form.credit_terms : undefined,
          credit_app: creditRequested
            ? {
                entity_type: form.entity_type,
                years_in_business: form.years_in_business,
                bank_name: form.bank_name,
                references: refs,
              }
            : undefined,
          terms_accepted: termsAccepted,
          terms_version: TERMS_VERSION,
          billing_address: addrBlob(billingAddr),
          delivery_address: addrBlob(effectiveDeliveries[0]!),
          billing_address_json: addrJson(billingAddr),
          delivery_address_json: addrJson(effectiveDeliveries[0]!),
          delivery_addresses: effectiveDeliveries.map((a, i) => ({
            label: effectiveDeliveries.length > 1 ? `Delivery ${i + 1}` : 'Delivery',
            ...addrJson(a),
          })),
          signature: { data: signatureUrl.replace(/^data:image\/png;base64,/, '') },
          hp_field: hpField, // honeypot
          turnstile_token: turnstileToken,
          invite_token: inviteToken || undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !data.ok) {
        setError(data.error ?? 'Something went wrong. Please try again.');
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch {
      setError('Could not reach the server. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-brix-surface flex items-start justify-center px-4 py-10">
      <ApplyBackdrop />

      <div className="relative w-full max-w-2xl">
        <div className="mb-8 flex justify-start">
          <BrixLogo variant="full" size="lg" showTagline showTrademark />
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-brix-border bg-brix-surface/85 backdrop-blur-xl shadow-card-deep">
          <div className="h-1.5 bg-brix-primary" aria-hidden />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-1.5 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
          />

          <div className="p-6">
            {done ? (
              <div className="text-center py-8">
                <CheckCircle2 size={44} className="text-brix-primary mx-auto mb-4" />
                <h1 className="font-display text-2xl font-semibold mb-2 text-brix-ink">
                  Thanks — we’ll be in touch
                </h1>
                <p className="text-sm text-brix-muted max-w-sm mx-auto">
                  We’ve received your application and sent a confirmation to your email. Our team
                  will review it and reach out shortly with next steps. Once your account is
                  approved, you’ll receive an email to log in and activate it.
                </p>
                <p className="text-sm text-brix-muted mt-4">
                  Questions? Call us at{' '}
                  <a href={SUPPORT_TEL_HREF} className="text-brix-primary font-semibold no-underline">
                    {SUPPORT_PHONE}
                  </a>
                  .
                </p>
              </div>
            ) : (
              <>
                <h1 className="font-display text-2xl font-semibold mb-1 text-brix-ink">
                  New account application
                </h1>
                <p className="text-sm text-brix-muted mb-6">
                  Tell us about your business and we’ll get your wholesale account set up.
                  Fields marked <span className="text-brix-warning">*</span> are required.
                </p>

                <form onSubmit={onSubmit} className="space-y-7" noValidate>
                  {/* Honeypot — display:none (not just off-screen), so autofill
                      engines never see or fill it. A field named/positioned like
                      "Company"/off-canvas-only is a well-known autofill trigger —
                      it must not read as one to a real browser. */}
                  <div aria-hidden style={{ display: 'none' }}>
                    <label htmlFor="hp_field">Leave this field blank</label>
                    <input
                      id="hp_field"
                      name="hp_field"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={hpField}
                      onChange={(e) => setHpField(e.target.value)}
                    />
                  </div>

                  {/* ---------------- Business ---------------- */}
                  <Section title="Business information">
                    <Field label="Business name" required>
                      <Input value={form.business_name} onChange={(e) => update('business_name', e.target.value)} autoFocus />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Primary contact name" required>
                        <Input value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} autoComplete="name" />
                      </Field>
                      <Field label="Email" required>
                        <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} autoComplete="email" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Phone">
                        <Input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} autoComplete="tel" />
                      </Field>
                      <Field label="Business type">
                        <Input value={form.business_type} onChange={(e) => update('business_type', e.target.value)} placeholder="Restaurant, café, retail…" />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Estimated monthly volume">
                        <Input value={form.est_volume} onChange={(e) => update('est_volume', e.target.value)} placeholder="e.g. 20 cases / 8 BIBs" />
                      </Field>
                      <Field label="DUNS number">
                        <Input value={form.duns_number} onChange={(e) => update('duns_number', e.target.value)} placeholder="9-digit D-U-N-S (if you have one)" />
                      </Field>
                    </div>
                    <StructuredAddressField
                      label="Billing address"
                      required
                      addr={billingAddr}
                      onChange={setBillingAddr}
                    />
                    <div className="space-y-3">
                      {deliveryAddrs.map((a, i) => (
                        <StructuredAddressField
                          key={i}
                          label={deliveryAddrs.length > 1 ? `Delivery location ${i + 1}` : 'Delivery address'}
                          hint={i === 0 ? 'Leave blank if it is the same as billing.' : undefined}
                          addr={a}
                          onChange={(next) =>
                            setDeliveryAddrs((ds) => ds.map((d, idx) => (idx === i ? next : d)))
                          }
                          onRemove={
                            deliveryAddrs.length > 1
                              ? () => setDeliveryAddrs((ds) => ds.filter((_, idx) => idx !== i))
                              : undefined
                          }
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => setDeliveryAddrs((ds) => [...ds, { ...EMPTY_ADDR }])}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brix-primary cursor-pointer"
                      >
                        <Plus size={12} /> Add another delivery location
                      </button>
                      <p className="text-[11px] text-brix-muted">
                        Each delivery location is set up as its own service location for routing
                        and deliveries.
                      </p>
                    </div>
                  </Section>

                  {/* ---------------- Tax documents ---------------- */}
                  {/* Tax ID required; resale permit optional, but leaving it off
                      needs an explicit "I understand" about sales tax (1.171). */}
                  <div ref={taxSectionRef}>
                  <Section
                    title="Tax ID & resale permit"
                    subtitle="Your Tax ID is required. Add your California resale (seller’s) permit number too — without one we have to charge sales tax. Photos or PDFs of each are optional but help."
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Tax ID (EIN)" required>
                        <Input
                          value={form.tax_id}
                          onChange={(e) => update('tax_id', e.target.value)}
                          placeholder="12-3456789"
                          required
                          inputMode="numeric"
                          autoComplete="off"
                        />
                      </Field>
                      <Field label="Resale permit # (CA seller’s permit)">
                        <Input
                          value={form.resale_permit_number}
                          onChange={(e) => update('resale_permit_number', e.target.value)}
                          placeholder="e.g. 123-456789"
                          autoComplete="off"
                        />
                      </Field>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <DocUpload
                        label="Tax ID document (optional)"
                        doc={taxIdDoc}
                        onAttach={setTaxIdDoc}
                        onError={setError}
                      />
                      <DocUpload
                        label="Resale certificate (optional)"
                        doc={resaleDoc}
                        onAttach={setResaleDoc}
                        onError={setError}
                      />
                    </div>
                    {/* The sales-tax miss is shown BY the amber box (turned red),
                        not repeated as a second line above it. */}
                    {taxFieldError && !ackMissing && (
                      <p className="text-sm font-semibold text-red-700" role="alert">{taxFieldError}</p>
                    )}
                    {showTaxAck && (
                      <div
                        className={`rounded-lg border px-3 py-3 space-y-2 ${
                          ackMissing ? 'border-red-400 bg-red-50' : 'border-brix-warning/40 bg-brix-warning/5'
                        }`}
                        role="alert"
                      >
                        <p className="flex items-start gap-2 text-sm font-semibold text-brix-warning leading-snug">
                          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                          {SALES_TAX_WARNING}
                        </p>
                        <p className="text-xs text-brix-muted leading-relaxed pl-6">
                          {ackMissing
                            ? 'Please do one or the other before submitting: enter your resale permit number above, or tick the box below.'
                            : 'Enter your resale permit number above, or confirm below that you understand.'}
                        </p>
                        <label className="flex items-start gap-2 pl-6 text-sm text-brix-ink cursor-pointer">
                          <input
                            type="checkbox"
                            checked={salesTaxAck}
                            onChange={(e) => {
                              setSalesTaxAck(e.target.checked);
                              setTaxFieldError(null);
                            }}
                            className="mt-0.5 h-4 w-4 accent-brix-primary"
                          />
                          <span>I understand — I don’t have a resale permit, and my invoices will include sales tax.</span>
                        </label>
                      </div>
                    )}
                  </Section>
                  </div>

                  {/* ---------------- Contacts ---------------- */}
                  <Section
                    title="Contacts"
                    subtitle="Who should we work with for each? Leave a card blank if it’s the same as your primary contact."
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Accounting contact">
                        <Input value={form.accounting_contact} onChange={(e) => update('accounting_contact', e.target.value)} placeholder="Name" />
                      </Field>
                      <Field label="Accounting email">
                        <Input type="email" value={form.accounting_email} onChange={(e) => update('accounting_email', e.target.value)} placeholder="ap@yourbusiness.com" />
                      </Field>
                    </div>
                    {(['billing', 'orders', 'management'] as const).map((role) => (
                      <div key={role} className="rounded-lg border border-brix-border px-3 py-3">
                        <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight mb-2 capitalize">
                          {role} contact
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <Input value={contacts[role].name} onChange={(e) => updateContact(role, 'name', e.target.value)} placeholder="Name" />
                          <Input type="email" value={contacts[role].email} onChange={(e) => updateContact(role, 'email', e.target.value)} placeholder="Email" />
                          <Input type="tel" value={contacts[role].phone} onChange={(e) => updateContact(role, 'phone', e.target.value)} placeholder="Phone" />
                        </div>
                      </div>
                    ))}
                  </Section>

                  {/* ---------------- Delivery ---------------- */}
                  <Section title="Delivery preferences">
                    <Field label="Delivery days / time windows">
                      <Textarea
                        value={form.delivery_windows}
                        onChange={(v) => update('delivery_windows', v)}
                        rows={2}
                        placeholder="e.g. Tuesdays & Fridays, 7–11am. Closed Mondays."
                      />
                    </Field>
                    <Field label="Special instructions">
                      <Textarea
                        value={form.special_instructions}
                        onChange={(v) => update('special_instructions', v)}
                        rows={2}
                        placeholder="e.g. Gate code #2714 — key hangs inside the side door; stock the back cooler."
                      />
                    </Field>
                  </Section>

                  {/* ---------------- Payment & credit ---------------- */}
                  <Section title="Payment & credit">
                    <div className="space-y-2">
                      <label className="flex items-start gap-2.5 rounded-lg border border-brix-border px-3 py-2.5 cursor-pointer">
                        <input
                          type="radio"
                          name="paytype"
                          checked={!creditRequested}
                          onChange={() => setCreditRequested(false)}
                          className="accent-brix-primary mt-0.5"
                        />
                        <span className="text-sm text-brix-ink">
                          <strong>Card / payment on delivery</strong>
                          <span className="block text-xs text-brix-muted mt-0.5">
                            Pay by card or ACH as you order — fastest way to get started.
                          </span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2.5 rounded-lg border border-brix-border px-3 py-2.5 cursor-pointer">
                        <input
                          type="radio"
                          name="paytype"
                          checked={creditRequested}
                          onChange={() => setCreditRequested(true)}
                          className="accent-brix-primary mt-0.5"
                        />
                        <span className="text-sm text-brix-ink">
                          <strong>Apply for credit terms</strong>
                          <span className="block text-xs text-brix-muted mt-0.5">
                            Net terms, subject to approval — requires three trade references below.
                          </span>
                        </span>
                      </label>
                    </div>

                    {creditRequested && (
                      <div className="space-y-4 rounded-lg border border-brix-primary/25 bg-brix-primary-tint/20 px-4 py-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <Field label="Requested terms" required>
                            <select
                              value={form.credit_terms}
                              onChange={(e) => update('credit_terms', e.target.value)}
                              className="w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink"
                            >
                              <option value="Net 15">Net 15</option>
                              <option value="Net 30">Net 30</option>
                            </select>
                          </Field>
                          <Field label="Entity type">
                            <select
                              value={form.entity_type}
                              onChange={(e) => update('entity_type', e.target.value)}
                              className="w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm text-brix-ink"
                            >
                              <option value="">Select…</option>
                              <option>Corporation</option>
                              <option>LLC</option>
                              <option>Partnership</option>
                              <option>Sole proprietor</option>
                            </select>
                          </Field>
                          <Field label="Years in business">
                            <Input value={form.years_in_business} onChange={(e) => update('years_in_business', e.target.value)} placeholder="e.g. 6" />
                          </Field>
                        </div>
                        <Field label="Bank name">
                          <Input value={form.bank_name} onChange={(e) => update('bank_name', e.target.value)} placeholder="Business bank" />
                        </Field>

                        <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
                          Trade references (3 required)
                        </div>
                        {refs.map((r, i) => (
                          <div key={i} className="rounded-lg border border-brix-border bg-brix-surface px-3 py-3">
                            <div className="text-[11px] font-semibold text-brix-muted mb-2">Reference {i + 1}</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <Input value={r.company} onChange={(e) => updateRef(i, 'company', e.target.value)} placeholder="Company / supplier" />
                              <Input value={r.contact} onChange={(e) => updateRef(i, 'contact', e.target.value)} placeholder="Contact name" />
                              <Input type="tel" value={r.phone} onChange={(e) => updateRef(i, 'phone', e.target.value)} placeholder="Phone" />
                              <Input type="email" value={r.email} onChange={(e) => updateRef(i, 'email', e.target.value)} placeholder="Email" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="text-xs text-brix-ink bg-brix-surface rounded-lg border border-brix-border px-3 py-2.5 leading-relaxed">
                      When your account is approved you will be able to set up your payment terms
                      through the portal.
                    </p>

                    <p className="text-xs text-brix-muted leading-relaxed">
                      Note: all customers that rent equipment or have equipment or cylinder
                      rentals must keep a card on file for cylinder rentals.
                    </p>
                  </Section>

                  {/* ---------------- Anything else ---------------- */}
                  <Section title="Anything else we should know?">
                    <Textarea value={form.notes} onChange={(v) => update('notes', v)} rows={3} />
                  </Section>

                  {/* ---------------- Terms ---------------- */}
                  <Section title="Terms & Conditions">
                    <div className="max-h-56 overflow-y-auto rounded-lg border border-brix-border bg-brix-surface px-4 py-3 text-xs text-brix-ink leading-relaxed space-y-3">
                      {APPLICATION_TERMS.map((s) => (
                        <div key={s.heading}>
                          <div className="font-semibold">{s.heading}</div>
                          {s.paragraphs.map((para, i) => (
                            <p key={i} className="mt-1 text-brix-muted">{para}</p>
                          ))}
                        </div>
                      ))}
                      <p className="text-brix-muted">Version {TERMS_VERSION} · Alameda Point Beverage Group Inc. (Brix Beverage)</p>
                    </div>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="accent-brix-primary mt-0.5"
                      />
                      <span className="text-sm text-brix-ink">
                        I have read and agree to the Terms &amp; Conditions above, and I certify the
                        information in this application is accurate. <span className="text-brix-warning">*</span>
                      </span>
                    </label>
                    <Field label="Authorized by (name & title)" required>
                      <Input
                        value={form.authorized_by}
                        onChange={(e) => update('authorized_by', e.target.value)}
                        placeholder="e.g. Jamie Doe, Owner"
                      />
                    </Field>
                    <Field label="Signature" required>
                      <div className="rounded-lg border border-brix-border bg-white h-32 overflow-hidden">
                        <SignaturePad ref={sigRef} onDirtyChange={setSigned} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-brix-muted">
                          Sign with your finger or mouse — this signature is attached to your application.
                        </span>
                        {signed && (
                          <button
                            type="button"
                            onClick={() => sigRef.current?.clear()}
                            className="text-[11px] text-brix-primary font-semibold cursor-pointer"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </Field>
                  </Section>

                  {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="pt-1" />}

                  {error && (
                    <div
                      role="alert"
                      className="rounded-lg border border-brix-warning/40 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning"
                    >
                      {error}
                    </div>
                  )}

                  <Button type="submit" variant="primary" size="lg" className="w-full" disabled={submitting}>
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                    {submitting ? 'Submitting…' : 'Submit application'}
                  </Button>

                  <p className="text-xs text-brix-muted text-center pt-1">
                    Already have an account?{' '}
                    <Link to="/login" className="text-brix-primary font-semibold no-underline">
                      Sign in
                    </Link>
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
